import React, { useEffect, useState } from 'react'
import { headTrackedCamera } from '../lib/headTrackedCamera'
import { resolveHeadTrackIndicator } from '../lib/headTrackIndicatorState'

const TONE_COLOR = {
  accent: 'var(--m-accent)',
  neutral: 'var(--m-fg-2)',
  tracking: 'var(--m-online)',
  warn: 'var(--m-warn)',
  danger: 'var(--m-danger)',
}

/**
 * HeadTrackIndicator — ENG-981 viewport pill. Renders ONLY an in-progress
 * session: 'starting…' and the tracking chip (face dot + Recenter + ⨯).
 * 'armed'/'blocked'/'error' deliberately render nothing here — the tool
 * rail's parallax button is their surface (start affordance, warn mark,
 * reason tooltip; see resolveParallaxRailState) — so a blueprint with an
 * enabled node doesn't park a permanent banner in everyone's viewport.
 * Every start()/retry path (here and the rail) fires exclusively from a real
 * click, never from an effect/timer/mount, so the camera-permission prompt
 * only ever appears in response to explicit user intent.
 *
 * Visual family: same fixed-pill treatment as the "tap to enable audio" pill
 * and the same viewport corner as `<TextureUpgradeIndicator />` (both in
 * App.jsx) — this reads as a sibling of both, not a new pattern.
 *
 * No effectPossible gate anymore: since <EditorHeadParallax> (Scene.jsx), the
 * offset applies in EVERY view — the rig-driven live camera via apply(), and
 * the OrbitControls-owned views (edit mode + live's rig-less fallback) via
 * applyHeadOffsetTo(). The whole-branch review's "webcam on with the offset
 * applied nowhere" hazard no longer has a reachable view, so the pill no
 * longer needs to know which view it's in. resolveHeadTrackIndicator keeps
 * the effectPossible parameter (unit-tested) in case a no-effect view ever
 * returns; nothing passes false today.
 */
export default function HeadTrackIndicator() {
  const [state, setState] = useState(headTrackedCamera.state)
  const [faceSeen, setFaceSeen] = useState(headTrackedCamera.faceSeen)
  const [errorMessage, setErrorMessage] = useState(headTrackedCamera.errorMessage)

  useEffect(() => {
    // headTrackedCamera._notify() fires on every state transition AND on a
    // faceSeen transition while already 'tracking' (see the transition
    // guards in headTrackedCamera.js `_onPose`/`_onFaceLost`) — so re-reading
    // faceSeen/errorMessage off the instance here, on every notify, is
    // sufficient. No polling: a lost/regained face while tracking now
    // notifies exactly like a state change would.
    const unsub = headTrackedCamera.onStateChange((next) => {
      setState(next)
      setFaceSeen(headTrackedCamera.faceSeen)
      setErrorMessage(headTrackedCamera.errorMessage)
    })
    return unsub
  }, [])

  const ui = resolveHeadTrackIndicator({ state, faceSeen, errorMessage })
  if (!ui.visible) return null

  const handleAction = (id) => {
    if (id === 'start' || id === 'retry') headTrackedCamera.start()
    else if (id === 'stop') headTrackedCamera.stop()
    else if (id === 'recenter') headTrackedCamera.recenter()
  }

  const color = TONE_COLOR[ui.tone] || 'var(--m-fg-2)'
  const pillStyle = {
    position: 'absolute', bottom: '9rem', left: '50%', transform: 'translateX(-50%)', flexShrink: 0,
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 14px', borderRadius: 999,
    background: 'color-mix(in srgb, var(--m-panel) 90%, transparent)', border: `1px solid ${color}`,
    color, fontFamily: 'var(--m-font)', fontSize: 11,
    pointerEvents: 'auto',
    // Above AvatarLoadOverlay's LoadProgressCard (zIndex 99999, App.jsx) — found
    // live: document.elementFromPoint at the pill's center returned the load
    // overlay while an avatar was (re)loading, making the pill unclickable with
    // no visible cause. Every other viewport overlay in App.jsx sits at 20-40,
    // so this is deliberately an outlier, not a new convention to match.
    zIndex: 100000,
  }

  return (
    <div data-testid="head-track-indicator" data-state={state} style={pillStyle}>
      {ui.dot && (
        <span
          data-testid="head-track-dot"
          style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: ui.dot === 'solid' ? color : 'transparent',
            border: `1px solid ${color}`,
          }}
        />
      )}
      <span>{ui.label}</span>
      {ui.actions.map((action) => (
        <button
          key={action.id}
          type="button"
          data-testid={`head-track-action-${action.id}`}
          onClick={() => handleAction(action.id)}
          title={action.id === 'stop' ? 'Stop head tracking' : action.id === 'recenter' ? 'Recenter head tracking' : undefined}
          style={{
            background: 'transparent', border: `1px solid ${color}`, borderRadius: 999,
            color, fontFamily: 'var(--m-font)', fontSize: 11, lineHeight: 1,
            padding: action.id === 'stop' ? '2px 6px' : '3px 8px',
            cursor: 'pointer', pointerEvents: 'auto', flexShrink: 0,
          }}
        >
          {action.label}
        </button>
      ))}
    </div>
  )
}
